import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsDropdownOption,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { JobProcess } from '../../../model/job-process.model';
import { JobprocessService } from '../jobprocess.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-jobprocess-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsDropdownComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './jobprocess-list.component.html',
  styleUrl: './jobprocess-list.component.scss',
})
export class JobprocessListComponent implements OnInit {
  readonly pageSize = 100;

  readonly takeOptions: readonly YdsDropdownOption[] = [
    { id: '10', label: '10' },
    { id: '100', label: '100' },
    { id: '1000', label: '1000' },
  ];

  readonly statusOptions: readonly YdsDropdownOption[] = [
    { id: 'Waiting', label: 'Waiting' },
    { id: 'Running', label: 'Running' },
    { id: 'Done', label: 'Done' },
    { id: 'Error', label: 'Error' },
  ];

  readonly tableColumns: YdsTableColumn[] = [
    { key: 'name', header: 'Job naam' },
    { key: 'description', header: 'Omschrijving' },
    { key: 'tenantCode', header: 'Tenant' },
    { key: 'notRestartable', header: 'Niet herstartbaar' },
    { key: 'status', header: 'Status' },
    { key: 'dateStart', header: 'Start datum' },
    { key: 'dateEnd', header: 'Eind datum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'details', label: 'Bekijk details' },
    { id: 'edit', label: 'Wijzigen' },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private jobprocessService = inject(JobprocessService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  jobs: WritableSignal<JobProcess[]> = signal([]);
  takeValue = signal('10');
  statusValue = signal<string | null>('');
  pageIndex = signal(0);
  isLoading = signal(true);

  private reload$ = new Subject<void>();

  pagedJobs = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.jobs().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedJobs().map((item) => ({
      id: item.jobProcessId,
      name: item.name,
      description: item.description ?? '',
      tenantCode: item.tenantCode ?? '',
      notRestartable: item.notRestartable,
      status: item.status ?? '',
      dateStart: this.formatDateTime(item.dateStart),
      dateEnd: this.formatDateTime(item.dateEnd),
    })),
  );

  private formatDateTime(value: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.jobs.set([]);
          return this.jobprocessService.getByContext(Number(this.takeValue()), this.statusValue() ?? '').pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.jobs.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.jobs.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadJobs();
  }

  loadJobs(): void {
    this.reload$.next();
  }

  onTakeChange(value: string | null): void {
    this.takeValue.set(value ?? '10');
    this.loadJobs();
  }

  onStatusChange(value: string | null): void {
    this.statusValue.set(value ?? '');
    this.loadJobs();
  }

  navigateToCreate(): void {
    this.router.navigate(['/proces/jobs/editable']);
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/proces/jobs/editable', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
    if (actionId === 'edit') {
      this.router.navigate(['/proces/jobs/editable', id], { queryParams: { edit: 1 } });
      return;
    }
    this.viewItem(id);
  }

  /** Running jobs cannot be deleted, so the delete action is shown disabled for them. */
  splitButtonItemsForRow(status: string): readonly YdsSplitButtonItem[] {
    if (status === 'Running') {
      return this.splitButtonItems.map((item) =>
        item.id === 'delete' ? { ...item, disabled: true } : item,
      );
    }
    return this.splitButtonItems;
  }

  private deleteItem(id: string): void {
    const job = this.jobs().find((item) => item.jobProcessId === id);
    if (job?.status === 'Running') {
      return;
    }

    this.dialogService.open({
      width: '320px',
      title: 'Verwijderen?',
      message: 'Job verwijderen?',
      cancelText: 'Nee',
      confirmText: 'Ja',
      variant: 'error',
    });

    this.dialogService
      .confirmed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.jobprocessService
            .removeJobProcess(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.loadJobs(),
            });
        }
      });
  }
}
